import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Loader,
  Paper,
  Stack,
  Text,
  useMantineColorScheme,
} from '@mantine/core';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapInputProps {
  value: { lat: number; lng: number } | null;
  onChange: (value: { lat: number; lng: number }) => void;
  isDisabled?: boolean;
}

// Component to handle map clicks
const MapClickHandler = ({
  onLocationSelect,
}: {
  onLocationSelect: (lat: number, lng: number) => void;
}) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

export const MapInput = ({ value, onChange, isDisabled }: MapInputProps) => {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // Romania center coordinates
  const romaniaCenter: [number, number] = [45.9432, 24.9668];
  const [position, setPosition] = useState<[number, number] | null>(
    value ? [value.lat, value.lng] : null
  );
  const [address, setAddress] = useState<string>('');
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);

  useEffect(() => {
    if (value) {
      setPosition([value.lat, value.lng]);
      fetchAddress(value.lat, value.lng);
    }
  }, [value]);

  const fetchAddress = async (lat: number, lng: number) => {
    setIsLoadingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      const data = await response.json();

      // Extract street name and number
      const { house_number, road, city, town, village } = data.address || {};
      const streetAddress = [house_number, road].filter(Boolean).join(' ');
      const locality = city || town || village || '';
      const fullAddress = [streetAddress, locality].filter(Boolean).join(', ');

      setAddress(fullAddress || data.display_name || 'Address not found');
    } catch (error) {
      console.error('Error fetching address:', error);
      setAddress('Unable to fetch address');
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    if (isDisabled) return;

    setPosition([lat, lng]);
    onChange({ lat, lng });
    fetchAddress(lat, lng);
  };

  return (
    <Box pos="relative" w="100%" h={400}>
      <MapContainer
        center={position || romaniaCenter}
        zoom={position ? 13 : 7}
        style={{ width: '100%', height: '100%', borderRadius: '8px' }}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution={
            isDark
              ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }
          url={
            isDark
              ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
              : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          }
        />

        {!isDisabled && (
          <MapClickHandler onLocationSelect={handleLocationSelect} />
        )}

        {position && <Marker position={position} />}
      </MapContainer>

      {position && (
        <Paper
          pos="absolute"
          bottom={10}
          left={10}
          p="xs"
          shadow="sm"
          style={{ zIndex: 1000, maxWidth: '300px' }}
        >
          <Stack gap={4}>
            {isLoadingAddress ? (
              <Loader size="xs" />
            ) : address ? (
              <>
                <Text size="xs" fw={700}>
                  {address}
                </Text>
              </>
            ) : null}
            <Text size="xs" c="dimmed">
              Lat: {position[0].toFixed(6)}, Lng: {position[1].toFixed(6)}
            </Text>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};
